import { useState } from "react";
import { ChevronRight } from "lucide-react";
import type { QuizQuestion } from "@shared/types";
import {
  TagPickerSheet,
  TextFieldSheet,
  OptionPickerSheet,
} from "./SelectorSheets";
import { InfoTip } from "./InfoTip";
import { Pill } from "../ux/Pill";

/**
 * A labelled profile field, styled to match the Connection Profile selectors
 * (`QuizFieldSelect`): an uppercase label over a glass "input" that shows the
 * current value inline — primary pills for multi-value answers, emoji + text
 * for single ones — with a chevron, and opens the matching bottom-sheet selector
 * to edit. Three kinds, picked by `kind`:
 *   • tags    — freeform multi-tag (Things you're into, Looking to, …)
 *   • text    — single free-text value (Education, Work)
 *   • options — single/multiple choice backed by a quiz question (Gender, …)
 */
type Base = { label: string; sheetTitle?: string };

type TagsField = Base & {
  kind: "tags";
  value: string[];
  onSave: (v: string[]) => void;
  suggestions: string[];
  max?: number;
  placeholder?: string;
};

type TextField = Base & {
  kind: "text";
  value: string | null;
  onSave: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
};

type OptionsField = Base & {
  kind: "options";
  question: QuizQuestion;
  value: unknown;
  onSave: (v: string | string[]) => void;
};

type Props = TagsField | TextField | OptionsField;

type DisplayItem = { label: string; emoji?: string };

/** Resolve a field's value into display items + whether to show them as pills. */
function resolveDisplay(props: Props): { items: DisplayItem[]; asPills: boolean } {
  if (props.kind === "tags") {
    return { items: props.value.map((v) => ({ label: v })), asPills: true };
  }
  if (props.kind === "text") {
    return { items: props.value ? [{ label: props.value }] : [], asPills: false };
  }
  const vals = Array.isArray(props.value)
    ? (props.value as string[])
    : props.value == null || props.value === ""
      ? []
      : [String(props.value)];
  const items = vals.map((v) => {
    const o = props.question.options?.find((opt) => opt.value === v);
    return { label: o?.label ?? String(v), emoji: o?.emoji };
  });
  return { items, asPills: props.question.type === "multiple" };
}

export function ProfileField(props: Props) {
  const [open, setOpen] = useState(false);
  const { items, asPills } = resolveDisplay(props);
  const hasValue = items.length > 0;
  const title = props.sheetTitle ?? props.label;
  const placeholder =
    (props.kind === "text" && props.placeholder) || "Select";

  return (
    <div className="space-y-1.5">
      <label className="input-label">{props.label}</label>

      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Edit ${props.label}`}
        className="input-glass-wrapper w-full flex items-center justify-between gap-3 text-left hover:border-primary-400 dark:hover:border-primary-500 transition-colors"
      >
        {!hasValue ? (
          <span className="text-neutral-400 dark:text-neutral-500 truncate">
            {placeholder}
          </span>
        ) : asPills ? (
          <span className="flex flex-wrap gap-1.5 min-w-0">
            {items.map((it) => (
              <Pill
                key={it.label}
                variant="soft"
                size="sm"
                leading={it.emoji ? <span>{it.emoji}</span> : undefined}
              >
                {it.label}
              </Pill>
            ))}
          </span>
        ) : (
          <span className="flex items-center gap-2 min-w-0 text-neutral-900 dark:text-neutral-50">
            {items[0].emoji && <span className="leading-none">{items[0].emoji}</span>}
            <span className="truncate">{items[0].label}</span>
          </span>
        )}
        <ChevronRight className="w-4 h-4 text-neutral-400 flex-shrink-0" />
      </button>

      {props.kind === "tags" && (
        <TagPickerSheet
          isOpen={open}
          onClose={() => setOpen(false)}
          title={title}
          value={props.value}
          onSave={props.onSave}
          suggestions={props.suggestions}
          max={props.max}
          placeholder={props.placeholder}
        />
      )}
      {props.kind === "text" && (
        <TextFieldSheet
          isOpen={open}
          onClose={() => setOpen(false)}
          title={title}
          value={props.value}
          onSave={props.onSave}
          placeholder={props.placeholder}
          maxLength={props.maxLength}
        />
      )}
      {props.kind === "options" && (
        <OptionPickerSheet
          isOpen={open}
          onClose={() => setOpen(false)}
          title={title}
          question={props.question}
          value={props.value}
          onSave={props.onSave}
        />
      )}
    </div>
  );
}

/**
 * A titled profile section (e.g. "About you", "Basic info") rendered in the same
 * header-over-divider style as the Connection Profile — a semibold title with the
 * ⓘ explainer, a rule that fills the rest of the row, and the fields beneath it
 * (no card chrome) so the editor reads as one continuous form.
 */
export function ProfileSection({
  title,
  children,
  layout = "stack",
  infoText = "This is your public profile — other members can see what you add here.",
}: {
  title: string;
  children: React.ReactNode;
  /** "stack" = one field per row; "grid" = two compact fields per row. */
  layout?: "stack" | "grid";
  /** Copy shown in the ⓘ explainer popup next to the section title. */
  infoText?: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-4">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          {title}
        </h2>
        <InfoTip title={title} text={infoText} />
        <div className="flex-1 h-px bg-neutral-200/80 dark:bg-neutral-800" />
      </div>
      <div
        className={
          layout === "grid"
            ? "grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5"
            : "space-y-5"
        }
      >
        {children}
      </div>
    </div>
  );
}
